import { useMemo } from 'react';

import { useManager } from '@jane/business-admin/data-access';
import { Permission } from '@jane/shared/auth';

// Returns undefined while useManager is loading so consumers can distinguish
// "loading" from "denied". Route guards should treat undefined as not-yet-ready
// (render null/loading state). UI element gates should treat undefined as true
// (show optimistically) using `value !== false` instead of `!!value`.
export const useHasPermissions = (
  permissions: Permission[] | undefined
): boolean | undefined => {
  const { data: currentManager, isFetched } = useManager();

  const hasPermission = useMemo(() => {
    if (!isFetched) return undefined;
    if (!permissions) return true;

    return permissions.some((permission) =>
      (currentManager?.manager?.permissions || []).includes(permission)
    );
  }, [isFetched, currentManager?.manager?.permissions, permissions]);

  return hasPermission;
};

const FULFILLMENT_ONLY_PERMISSIONS: Permission[] = [
  Permission.EditFulfillments,
  Permission.AssignFulfillments,
];

export const useOnlyFulfillmentsPermission = (): boolean | undefined => {
  const { data: currentManager, isFetched } = useManager();

  return useMemo(() => {
    if (!isFetched) return undefined;

    const permissions = currentManager?.manager?.permissions || [];
    return (
      permissions.length > 0 &&
      permissions.every((p) =>
        FULFILLMENT_ONLY_PERMISSIONS.includes(p as Permission)
      )
    );
  }, [isFetched, currentManager?.manager?.permissions]);
};

export const useOnlyAnalyticsPermission = () => {
  const { data: currentManager, isFetched } = useManager();

  const hasPermission = useMemo(() => {
    if (!isFetched) return false;

    const permissions = currentManager?.manager?.permissions || [];
    return (
      permissions.length === 1 && permissions.includes(Permission.ViewAnalytics)
    );
  }, [isFetched, currentManager?.manager?.permissions]);

  return hasPermission;
};
